import type { Campaigns } from '@gonfalon/billing/campaigns';
import type { PlanLimitsType } from '@gonfalon/billing/types';
import { BillingIntervals, LimitNames, PlanTypes } from '@gonfalon/billing/types';
import { isPostSubscriptionPreviewRequestBody, isPutSubscriptionRequestBody } from '@gonfalon/billing/utils';
import {
  enableBillingOpenapiMigrationUI,
  enableSelfServeExperimentationKeysLimit,
  enableSelfServeObservability,
  isEnableStripePromoCodesEnabled,
} from '@gonfalon/dogfood-flags/dogfood-flags';
// eslint-disable-next-line no-restricted-imports
import type { List, Map } from '@gonfalon/immutable-shim/immutableShim';
// oxlint-disable-next-line no-restricted-imports
import { fromJS, OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
import http, { jsonToImmutable, jsonToImmutableError } from '@gonfalon/legacy-http-client/httpUtils';
import type { ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';
import { getPaymentCard as getPaymentCardOpenapi } from '@gonfalon/openapi/operations/getPaymentCard';
import { postCardAdded } from '@gonfalon/openapi/operations/postCardAdded';
import { postSubscriptionPreview } from '@gonfalon/openapi/operations/postSubscriptionPreview';
import { putSubscription } from '@gonfalon/openapi/operations/putSubscription';
import { isFoundationPlanNotFacade, isFreeDevPlan } from '@gonfalon/plan-manager/utils';
import { normalize, schema } from 'normalizr';

import type {
  AccruedInvoice,
  Invoice,
  LegacyPlan,
  LegacySubscription,
  LimitPlans,
  PaymentCard,
  PlanList,
  Subscription,
  SubscriptionChange,
  SubscriptionChangeType,
  SubscriptionPreview,
  SubscriptionUsage,
} from '#utils/billingUtils';
import {
  createAccruedInvoice,
  createInvoice,
  createLegacyPlan,
  createLegacySubscription,
  createLimitPlans,
  createPaymentCard,
  createPlanList,
  createSubscription,
  createSubscriptionUsage,
} from '#utils/billingUtils';
import { convertMapToOrderedMap } from '#utils/collectionUtils';
import { addQueryParams } from '#utils/urlUtils';

export function getLegacyPlans(): Promise<List<LegacyPlan>> {
  return http
    .get('/internal/plans')
    .then(jsonToImmutable)
    .then((data) => data.get('items').map(createLegacyPlan))
    .catch(jsonToImmutableError);
}

export function getPlans(): Promise<PlanList> {
  return http.get('/internal/billingv2/plans').then(jsonToImmutable).then(createPlanList).catch(jsonToImmutableError);
}

export const createSubscriptionCampaign = (campaign: Campaigns): Promise<Subscription> =>
  http
    .patch('/internal/billingv2/account/subscription/campaigns', {
      body: {
        op: 'add',
        value: campaign,
      },
    })
    .then(jsonToImmutable)
    .then(createSubscription)
    .catch(jsonToImmutableError);

export function getLimitPlans(planType = PlanTypes.PROFESSIONAL): Promise<LimitPlans> {
  // Fixed plans (DEVELOPER, DEVELOPER2025) don't have configurable limits endpoints
  // Redirect DEVELOPER to FOUNDATION2023 for limit data
  let targetPlanType = planType;
  if (planType === PlanTypes.DEVELOPER) {
    targetPlanType = PlanTypes.FOUNDATION2023;
  }

  return http
    .get(`/internal/billingv2/plans/${targetPlanType}/limits`)
    .then(jsonToImmutable)
    .then((data) => createLimitPlans(data.get('plans')))
    .catch(jsonToImmutableError);
}

export function getLegacySubscription(): Promise<LegacySubscription> {
  // NOTE: the app nav uses this api, but not all pages have it in context
  // so hardcode it for now.
  return http
    .get('/internal/account/subscription')
    .then(jsonToImmutable)
    .then(createLegacySubscription)
    .catch(jsonToImmutableError);
}

export function getSubscription(): Promise<Subscription> {
  return http
    .get('/internal/billingv2/account/subscription')
    .then(jsonToImmutable)
    .then(createSubscription)
    .catch(jsonToImmutableError);
}

export function getSubscriptionUsage(): Promise<SubscriptionUsage> {
  return http
    .get('/internal/billingv2/account/subscription/usage')
    .then(jsonToImmutable)
    .then((data) => createSubscriptionUsage(data.get('_usage')))
    .catch(jsonToImmutableError);
}

export async function getPaymentCard(): Promise<PaymentCard | null> {
  if (enableBillingOpenapiMigrationUI()) {
    const response = await getPaymentCardOpenapi({});
    if (response.data) {
      return createPaymentCard(fromJS(response.data));
    } else if (response.error) {
      if (response.response.status === 404) {
        // 404 indicates the account does not yet have a card
        return null;
      } else {
        throw fromJS(response.error);
      }
    }
  }

  return (
    http
      .get('/internal/account/card')
      .then(jsonToImmutable)
      .then(createPaymentCard)
      // 404 indicates the account does not yet have a card
      .catch((resp) => (resp.status === 404 ? Promise.resolve(null) : jsonToImmutableError(resp)))
  );
}

export const postSetupIntent = async () => {
  const response = await http
    .post('/internal/billingv2/card-setup-intent', {
      body: '',
    })
    .then(jsonToImmutable, jsonToImmutableError);
  return response.get('clientSecret');
};

export const postAddedCard = async (paymentMethodId: string) => {
  if (enableBillingOpenapiMigrationUI()) {
    const response = await postCardAdded({ body: { paymentMethodId } });
    if (response.data) {
      return createPaymentCard(fromJS(response.data));
    } else {
      throw fromJS(response.error);
    }
  }

  return http
    .post('/internal/billingv2/card-added', {
      body: { paymentMethodId },
    })
    .then(jsonToImmutable)
    .then(createPaymentCard)
    .catch(jsonToImmutableError);
};

export function setPaymentCard(stripeToken: string): Promise<PaymentCard> {
  return http
    .put('/internal/account/card', {
      body: JSON.stringify({ stripeToken }),
    })
    .then(jsonToImmutable)
    .then(createPaymentCard)
    .catch(jsonToImmutableError);
}

const invoices = new schema.Entity('invoices', {}, { idAttribute: 'key' });

type NormalizedInvoices = ImmutableMap<{
  result: ImmutableMap<{
    items: List<string>;
  }>;
  entities: ImmutableMap<{
    invoices: Map<string, Invoice>;
  }>;
}>;

export function getInvoices(): Promise<OrderedMap<string, Invoice>> {
  return http.get('/internal/account/invoices').then(
    (res) =>
      res.json().then((data) => {
        const normalized: NormalizedInvoices = fromJS(normalize(data, { items: [invoices] }));
        return normalized.withMutations((map) => {
          map.updateIn(['entities', 'invoices'], (entities: Map<string, Invoice>) =>
            convertMapToOrderedMap(entities, normalized.getIn(['result', 'items'])),
          );
          map.updateIn(['entities', 'invoices'], (entities) => (entities ? entities.map(createInvoice) : OrderedMap()));
        });
      }),
    jsonToImmutableError,
  );
}

const accruedInvoices = new schema.Entity('accruedInvoices', {}, { idAttribute: 'id' });

type NormalizedAccruedInvoices = ImmutableMap<{
  result: ImmutableMap<{
    items: List<string>;
  }>;
  entities: ImmutableMap<{
    accruedInvoices: Map<string, AccruedInvoice>;
  }>;
}>;

export function getAccruedInvoices(): Promise<OrderedMap<string, AccruedInvoice>> {
  return http.get('/internal/account/accrued-invoices').then(
    (res) =>
      res.json().then((data) => {
        const normalized = normalize(data, { items: [accruedInvoices] });
        const immutable: NormalizedAccruedInvoices = fromJS(normalized);
        const mutatedImmutable = immutable.withMutations((map) => {
          map.updateIn(['entities', 'accruedInvoices'], (acInvoices: Map<string, AccruedInvoice>) =>
            convertMapToOrderedMap(acInvoices, immutable.getIn(['result', 'items'])),
          );
          map.updateIn(['entities', 'accruedInvoices'], (entities) =>
            entities ? entities.map(createAccruedInvoice) : OrderedMap(),
          );
        });
        return mutatedImmutable;
      }),
    jsonToImmutableError,
  );
}

export async function subscribe({
  subscription,
  stripeToken,
  prorationTime,
  promoCode,
  enableSelfServeUBBWithAnnualCommits,
  metadata = {},
}: {
  subscription: SubscriptionChange;
  stripeToken?: string;
  prorationTime: number;
  promoCode?: string;
  enableSelfServeUBBWithAnnualCommits?: boolean;
  metadata?: { isDeveloperSignup?: boolean };
}): Promise<Subscription> {
  const sendPromoCode = promoCode !== undefined && promoCode !== '' && isEnableStripePromoCodesEnabled();
  let url = '/internal/billingv2/account/subscription';

  const nonFoundation = {
    ...(subscription.toRep() as Partial<SubscriptionChangeType>),
    stripeToken,
    prorationTime,
  };

  const monthlyFoundationPlanBody = {
    planType: subscription.planType,
    billingInterval: subscription?.billingInterval,
    metadata,
  };

  const annualFoundationPlanLimits = () => {
    const limits: Partial<PlanLimitsType> = {
      Hosts: subscription?.limits.Hosts,
      MonthlyActiveUsers: subscription?.limits.MonthlyActiveUsers,
    };

    if (enableSelfServeExperimentationKeysLimit()) {
      limits[LimitNames.EXPERIMENTATION_KEYS] = subscription?.limits.ExperimentationKeys;
    }

    if (enableSelfServeObservability()) {
      limits[LimitNames.SESSIONS] = subscription?.limits.Sessions;
      limits[LimitNames.ERRORS] = subscription?.limits.Errors;
      limits[LimitNames.TRACES] = subscription?.limits.Traces;
      limits[LimitNames.LOGS] = subscription?.limits.Logs;
    }

    return limits;
  };

  const annualFoundationPlanBody = {
    ...monthlyFoundationPlanBody,
    billingProcess: subscription?.billingProcess,
    limits: annualFoundationPlanLimits(),
    prorationTime,
  };

  const isFoundation = isFoundationPlanNotFacade(subscription?.planType);

  const foundationPlan =
    isFoundation && subscription?.billingInterval === BillingIntervals.YEARLY && enableSelfServeUBBWithAnnualCommits
      ? annualFoundationPlanBody
      : monthlyFoundationPlanBody;

  // Free developer plans are fixed plans - send minimal body (no limits)
  const freeDevPlanBody = {
    planType: subscription.planType,
    billingInterval: subscription.billingInterval,
    metadata,
  };

  const body = isFoundation ? foundationPlan : isFreeDevPlan(subscription?.planType) ? freeDevPlanBody : nonFoundation;

  if (enableBillingOpenapiMigrationUI()) {
    if (!isPutSubscriptionRequestBody(body)) {
      throw new Error('Invalid body');
    }

    const response = await putSubscription({
      body,
      query: sendPromoCode ? { promo: promoCode } : undefined,
    });

    if (response.data) {
      return createSubscription(fromJS(response.data));
    } else {
      throw fromJS(response.error);
    }
  }

  if (sendPromoCode) {
    url = addQueryParams(url, { promo: promoCode });
  }

  return http
    .put(url, {
      body,
    })
    .then(jsonToImmutable)
    .then(createSubscription)
    .catch(jsonToImmutableError);
}

export function deleteSubscription(): Promise<void> {
  return http.delete('/internal/account/subscription').catch(jsonToImmutableError);
}

export async function previewSubscriptionPrice(
  subscription: SubscriptionChange,
  promoCode?: string,
): Promise<SubscriptionPreview> {
  const sendPromoCode = promoCode !== undefined && promoCode !== '' && isEnableStripePromoCodesEnabled();

  if (enableBillingOpenapiMigrationUI()) {
    const body = subscription.toRep();

    if (!isPostSubscriptionPreviewRequestBody(body)) {
      throw new Error('Invalid body');
    }

    const response = await postSubscriptionPreview({
      body,
      query: sendPromoCode ? { promo: promoCode } : undefined,
    });

    if (response.data) {
      return fromJS(response.data);
    } else {
      throw fromJS(response.error);
    }
  }

  let url = '/internal/billingv2/account/subscription';
  const body: object & { promo?: string } = {
    ...subscription.toRep(),
  };

  if (sendPromoCode) {
    url = addQueryParams(url, { promo: promoCode });
  }

  return http
    .report(url, {
      body,
    })
    .then(jsonToImmutable, jsonToImmutableError);
}
